import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useContent } from "../../hooks/useContent";
import CustomMUITable from "../../Components/GeneralComponents/CustomMUITable/CustomMUITable";
import PageLoader from "../../Components/GeneralComponents/PageLoader";
import { get_companies_api, get_company_delete_api } from "../../DAL/Company";
import ConfirmationPopup from "../../Components/GeneralComponents/ConfirmationPopup";

const ManageCompanies = () => {
  const navigate = useNavigate();
  const { setPageHeading } = useContent();
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("company_name");
  const [isLoadingPage, setIsLoadingPage] = useState(true);
  const [updatedCompanies, setUpdatedCompanies] = useState([]);
  const [openDelete, setOpenDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedObject, setSelectedObject] = useState({});

  const loadCompanies = async (e) => {
    if (e) e.preventDefault();
    setIsLoadingPage(true);
    const result = await get_companies_api({ search, sort });
    if (result.code === 200) {
      const data = result.companies || [];
      const numbered = data.map((c, index) => ({
        ...c,
        number: index + 1,
        // Format license dates for display
        start_date: c.start_date
          ? dayjs(c.start_date).format("DD/MM/YYYY")
          : "",
        end_date: c.end_date ? dayjs(c.end_date).format("DD/MM/YYYY") : "",
      }));
      setUpdatedCompanies(numbered);
    } else {
      toast.error(`${result.message}`);
    }
    setIsLoadingPage(false);
  };

  useEffect(() => {
    setPageHeading("Existing Company Licenses");
    loadCompanies();
  }, []);

  const handleChangeSearch = (e) => setSearch(e.target.value);
  const handleChangeSort = (e) => setSort(e.target.value);

  const handleSubmit = (e) => {
    e.preventDefault();
    loadCompanies();
  };
  const handleBack = () => navigate("/home");
  const handleCreate = () => navigate("/create-company");

  const handleEditCompany = (company) => {
    navigate(`/manage-companies/update-company/${company.company_id}`);
  };

  const handleDeleteCompany = async () => {
    setIsDeleting(true);
    const result = await get_company_delete_api(selectedObject.company_id);
    if (result.code === 200) {
      setUpdatedCompanies((old) =>
        old.filter(
          (company) => company.company_id !== selectedObject.company_id
        )
      );
      setIsDeleting(false);
      setOpenDelete(false);
      setSelectedObject(null);
      toast.success(`${result.message}`);
    } else {
      toast.error(`Error deleting company: ${result.message}`);
    }
  };

  const handleDeleteClick = (item) => {
    setSelectedObject(item);
    setOpenDelete(true);
  };

  const MENU_OPTIONS = [
    {
      label: "Edit",
      icon: "mdi:pencil",
      handleClick: handleEditCompany,
    },
    {
      label: "Delete",
      icon: "mdi:trash-can",
      handleClick: handleDeleteClick,
    },
  ];

  if (isLoadingPage) return <PageLoader />;

  const TABLE_HEAD = [
    { id: "number", label: "#", type: "number" },
    { id: "company_name", label: "Company Name" },
    { id: "admin_username", label: "Admin Username" },
    { id: "status", label: "Status" },
    { id: "number_of_licenses_assigned", label: "Licenses" },
    { id: "start_date", label: "Start Date" },
    { id: "end_date", label: "End Date" },
    { id: "company_web_site", label: "Website" },
    { id: "actions", label: "Actions", type: "action" },
  ];

  return (
    <div className="login-page">
      <div className="d-flex justify-content-center">
        <div className="container mx-auto">
          <div className="form-wrapper">
            <form onSubmit={handleSubmit}>
              <div className="row mb-3">
                <div className="col">
                  <div className="col">
                    <div className="d-flex justify-content-between">
                      <button
                        type="button"
                        className="cancel-button"
                        onClick={handleBack}
                      >
                        <Icon icon="mdi:arrow-left" /> Back
                      </button>
                      <button
                        type="button"
                        className="theme-button"
                        onClick={handleCreate}
                      >
                        <Icon icon="mdi:plus" /> Create Company Account
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col">
                  <div className="search_sort_box d-flex gap-2 align-items-center">
                    <div className="form-group flex-1">
                      <label>Search</label>
                      <div className="input-wrapper">
                        <Icon icon="mdi:magnify" className="input-icon" />
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search companies..."
                          value={search}
                          onChange={handleChangeSearch}
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Sort By</label>
                      <div className="input-wrapper">
                        <Icon
                          icon="mdi:filter-variant"
                          className="input-icon"
                        />
                        <select
                          className="form-select"
                          value={sort}
                          onChange={handleChangeSort}
                        >
                          <option value="company_name">Company Name</option>
                          <option value="status">Status</option>
                          <option value="created_at">Created Date</option>
                        </select>
                      </div>
                    </div>
                    <div className="mt-1">
                      <button type="submit" className="theme-button mt-4">
                        Go
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <CustomMUITable
                TABLE_HEAD={TABLE_HEAD}
                data={updatedCompanies}
                MENU_OPTIONS={MENU_OPTIONS}
                hide_search={true}
              />
              <ConfirmationPopup
                openDelete={openDelete}
                setOpenDelete={setOpenDelete}
                title={"Are you sure you want to delete this company?"}
                handleAgree={handleDeleteCompany}
                isLoading={isDeleting}
              />
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageCompanies;
